import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { IAHJ } from "@/types/ahj";
import { format } from "date-fns";
import {
  Pencil,
  Loader2,
  FileText,
  CheckCircle,
  AlertCircle,
  MapPin,
  Building2,
  Check,
  ExternalLink,
  Clock,
  Zap,
  DollarSign,
  Upload,
} from "lucide-react";
import { AHJFormDialog } from "./AHJFormDialog";
import { useQuery } from "@tanstack/react-query";
import { ahjApi } from "@/services/ahjService";
import { IAHJDocument } from "@/dto/get-project-response.dto";
import { cn } from "@/lib/utils";

const permittingRequirementsLabels: Record<string, string> = {
  slabUpliftTest: "Slab Uplift Test",
  slabCoreTest: "Slab Core Test",
  adaPlans: "ADA Plans",
  photometricPlans: "Photometric Plans",
  egressPlans: "Egress Plans",
  smokeVentInspections: "Smoke Vent Inspections",
  fpeStamp: "FPE Stamp",
  weightSigns: "Weight Signs",
  flueKeepersPalletStops: "Flue Keepers Pallet Stops",
  keepClearsStickers: "Keep Clears Stickers",
  fireSignsCommoditySigns: "Fire Signs/Commodity Signs",
};

interface AHJDetailsModalProps {
  ahj: IAHJ | Partial<IAHJ> | Record<string, unknown> | null | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showEditButton?: boolean;
  onEdit?: (ahj: IAHJ) => void;
  projectDocuments?: IAHJDocument[];
  onUpload?: (requirementKey: string) => void;
}

export const AHJDetailsModal = ({
  ahj,
  open,
  onOpenChange,
  showEditButton = false,
  onEdit,
  projectDocuments,
  onUpload,
}: AHJDetailsModalProps) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const ahjId = ahj?._id as string | undefined;

  // Fetch full AHJ data when edit dialog needs to open
  const {
    data: fullAHJResponse,
    isLoading: isLoadingFullAHJ,
    refetch: refetchFullAHJ,
  } = useQuery({
    queryKey: ["ahj", ahjId],
    queryFn: () => ahjApi.getAHJ(ahjId!),
    enabled: false, // Only fetch when we explicitly call refetch
  });

  const fullAHJ = fullAHJResponse?.ahj;

  if (!ahj) return null;

  const activeRequirements = Object.entries(
    ahj?.permittingRequirements || {}
  ).filter(([_, value]) => value);

  const handleEdit = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (ahjId) {
      // Fetch the full AHJ data before opening edit dialog
      const result = await refetchFullAHJ();
      if (result.data?.ahj) {
        setEditDialogOpen(true);
      }
    }
  };

  const handleEditDialogClose = (open: boolean) => {
    setEditDialogOpen(open);
  };

  const handleEditSuccess = (updatedAhj: IAHJ) => {
    setEditDialogOpen(false);
    onOpenChange(false);
    // Call the onEdit callback if provided
    if (onEdit) {
      onEdit(updatedAhj);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          id="ahj-details-modal"
          className="max-w-2xl max-h-[80vh] overflow-y-auto border-border/50 shadow-sophisticated"
        >
          <DialogHeader className="border-b border-border/50 pb-4">
            <div className="flex items-center justify-between pr-4">
              <div className="flex items-center gap-2">
                <DialogTitle className="text-xl font-semibold">AHJ Details</DialogTitle>
              </div>
              {showEditButton && (
                <Button
                  id="ahj-edit-button"
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleEdit}
                  disabled={isLoadingFullAHJ}
                  className="gap-2 shrink-0 hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  {isLoadingFullAHJ ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Pencil className="h-4 w-4" />
                  )}
                  Edit
                </Button>
              )}
            </div>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Key Details Section */}
            <div
              id="ahj-key-details"
              className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-muted/10 rounded-lg border border-border/50"
            >
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Name
                </span>
                <span className="font-semibold text-base">
                  {String(ahj?.name || "")}
                </span>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Type
                </span>
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="capitalize font-medium">
                    {String(ahj?.type || "")}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  State
                </span>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">
                    {String(ahj?.state || "")}
                  </span>
                </div>
              </div>
            </div>

            {(ahj?.requiredSubmittalProcess ||
              ahj?.avgReviewTimeline ||
              ahj?.expeditedServices) && (
              <div className="space-y-4">
                <Separator />
                <div className="grid gap-6">
                  {ahj?.requiredSubmittalProcess && (
                    <div id="ahj-submittal-process">
                      <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 text-foreground/90">
                        <FileText className="h-4 w-4 text-primary" />
                        Required Submittal Process
                      </h4>
                      <div
                        className="prose prose-sm max-w-none dark:prose-invert
                        prose-headings:font-semibold prose-headings:tracking-tight prose-headings:text-foreground prose-headings:mb-2 prose-headings:mt-4 first:prose-headings:mt-0
                        prose-h1:text-xl prose-h2:text-lg prose-h3:text-base
                        prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:my-2
                        prose-ul:my-2 prose-ul:list-disc prose-ul:pl-4
                        prose-ol:my-2 prose-ol:list-decimal prose-ol:pl-4
                        prose-li:text-muted-foreground prose-li:my-1 prose-li:marker:text-muted-foreground/60
                        prose-strong:font-semibold prose-strong:text-foreground
                        prose-a:text-primary prose-a:underline-offset-4 prose-a:font-medium hover:prose-a:text-primary/80
                        prose-blockquote:border-l-2 prose-blockquote:border-primary/50 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-muted-foreground
                        prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:text-xs prose-code:font-mono prose-code:text-foreground
                        prose-pre:bg-muted prose-pre:p-4 prose-pre:rounded-lg prose-pre:border
                        prose-img:rounded-lg prose-img:border prose-img:shadow-sm prose-img:my-4 prose-img:max-w-full
                        bg-card/50 p-5 rounded-lg border border-border/50 shadow-sm"
                        dangerouslySetInnerHTML={{
                          __html: ahj?.requiredSubmittalProcess || "",
                        }}
                        onClick={(e) => {
                          const target = e.target as HTMLElement;
                          if (target.tagName === "IMG") {
                            const img = target as HTMLImageElement;
                            window.open(img.src, "_blank");
                          }
                        }}
                      />
                    </div>
                  )}

                  {(ahj?.avgReviewTimeline ||
                    ahj?.expeditedServices) && (
                    <div
                      id="ahj-timeline-expedited"
                      className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                    >
                      {ahj?.avgReviewTimeline && (
                        <div className="p-4 rounded-lg border border-border/50 bg-card/50 shadow-sm">
                          <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Average Review Timeline
                          </h4>
                          <p className="font-medium text-foreground">
                            {String(ahj?.avgReviewTimeline)}
                          </p>
                        </div>
                      )}
                      {ahj?.expeditedServices && (
                        <div className="p-4 rounded-lg border border-border/50 bg-card/50 shadow-sm">
                          <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                            <Zap className="h-4 w-4" />
                            Expedited Services
                          </h4>
                          <div className="flex flex-col gap-1">
                            <p className="font-medium text-foreground">
                              {String(ahj?.expeditedServices)}
                            </p>
                            {ahj?.expeditedServicesCost && (
                              <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                                <DollarSign className="h-3 w-3" />
                                <span>
                                  Cost:{" "}
                                  {String(ahj?.expeditedServicesCost)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeRequirements.length > 0 && (
              <div id="ahj-required-documents" className="space-y-4">
                <Separator className="bg-border/50" />
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold flex items-center gap-2 text-foreground/90">
                    <FileText className="h-4 w-4 text-primary" />
                    Additional Required Documents
                  </h4>
                  <Badge variant="outline" className="text-xs font-normal bg-muted/50">
                    {activeRequirements.length} Items
                  </Badge>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {activeRequirements.map(([key]) => {
                    const doc = projectDocuments?.find(
                      (d: IAHJDocument) => d.requirementKey === key
                    );

                    return (
                      <div
                        key={key}
                        className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-card/50 hover:bg-accent/50 transition-colors group shadow-sm"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={cn(
                              "p-2 rounded-full flex items-center justify-center shadow-sm",
                              doc
                                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                            )}
                          >
                            {doc ? (
                              <Check className="h-4 w-4" />
                            ) : (
                              <AlertCircle className="h-4 w-4" />
                            )}
                          </div>
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium">
                              {permittingRequirementsLabels[key] || key}
                            </span>
                            <span
                              className={cn(
                                "text-xs",
                                doc
                                  ? "text-green-600 dark:text-green-400"
                                  : "text-amber-600 dark:text-amber-400"
                              )}
                            >
                              {doc ? "Uploaded" : "Pending Upload"}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {doc ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                asChild
                                className="h-8"
                              >
                                <a
                                  href={doc.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex items-center gap-1.5"
                                >
                                  View
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              </Button>
                              {onUpload && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                  onClick={() => onUpload(key)}
                                  title="Manage file"
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                              )}
                            </>
                          ) : (
                            onUpload && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 gap-1.5"
                                onClick={() => onUpload(key)}
                              >
                                <Upload className="h-3 w-3" />
                                Upload
                              </Button>
                            )
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {ahj?.createdBy &&
              typeof ahj.createdBy === "object" &&
              "firstName" in ahj.createdBy && (
                <div className="pt-4 border-t border-border/50 text-xs text-muted-foreground">
                  <p>
                    Created by {String(ahj.createdBy.firstName || "")}{" "}
                    {String(
                      "lastName" in ahj.createdBy ? ahj.createdBy.lastName : ""
                    )}{" "}
                    on{" "}
                    {ahj?.createdAt &&
                      format(new Date(ahj.createdAt as string), "MMM d, yyyy")}
                  </p>
                  {ahj?.updatedBy &&
                    typeof ahj.updatedBy === "object" &&
                    "firstName" in ahj.updatedBy && (
                      <p className="mt-1">
                        Last updated by {String(ahj.updatedBy.firstName || "")}{" "}
                        {String(
                          "lastName" in ahj.updatedBy
                            ? ahj.updatedBy.lastName
                            : ""
                        )}{" "}
                        on{" "}
                        {ahj?.updatedAt &&
                          format(
                            new Date(ahj.updatedAt as string),
                            "MMM d, yyyy"
                          )}
                      </p>
                    )}
                </div>
              )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog - Show when we have full AHJ data loaded */}
      {fullAHJ && (
        <AHJFormDialog
          open={editDialogOpen}
          onOpenChange={handleEditDialogClose}
          editingAHJ={fullAHJ}
          onSuccess={handleEditSuccess}
        />
      )}
    </>
  );
};
